import { getCsrfToken } from '../utils/helpers.js';
import { bookingFlow } from '../components/bookingFlow.js';
import Alpine from 'alpinejs';

export default function listingBooking(options = {}) {
    return {
        ...bookingFlow.init(options),
        debugPricing: !!options.debugPricing,
        
        // Dynamic pricing state
        nightlyRate: 0,
        total: 0,
        taxesAndFees: 0,
        minStayNights: 1,
        currency: 'USD',
        isCalculated: false,
        basePrice: options.basePrice || 0,
        quote: null,
        isSubmitting: false,
        
        // CSRF state synchronization
        csrfReady: false,

        getMaxGuests() {
            const parsed = Number(options.maxGuests);
            return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null;
        },

        getTotalGuests() {
            const adults = Number.isFinite(Number(this.adults)) ? Number(this.adults) : 0;
            const children = Number.isFinite(Number(this.children)) ? Number(this.children) : 0;
            return adults + children;
        },

        validateGuestCount() {
            const maxGuests = this.getMaxGuests();
            if (!maxGuests) {
                return null;
            }

            const totalGuests = this.getTotalGuests();
            if (totalGuests > maxGuests) {
                const guestLabel = maxGuests === 1 ? 'guest' : 'guests';
                return `This property allows up to ${maxGuests} ${guestLabel}.`;
            }

            return null;
        },

        clearGuestCountError() {
            if (!this.errors?.general || !this.errors.general.startsWith('This property allows up to ')) {
                return;
            }

            const nextErrors = { ...(this.errors || {}) };
            delete nextErrors.general;
            this.errors = nextErrors;
        },
        
        init() {
            // Watch global search store for changes
            this.$watch('$store.search.dates', (val) => this.syncDates(val));
            this.$watch('$store.search.guests', (val) => this.syncGuests());
            this.$watch('$store.search.adults', (val) => this.syncGuests());
            this.$watch('$store.search.children', (val) => this.syncGuests());
            
            // Watch internal state changes to trigger pricing update
            this.$watch('adults', () => this.updatePricing());
            this.$watch('children', () => this.updatePricing());
            
            // Initial sync from store (which is initialized from URL)
            const searchStore = Alpine.store('search');
            if (searchStore) {
                // If no dates set in store (not from URL), set default: Tomorrow -> +3 nights
                if (!searchStore.hasDates()) {
                    this.skipNextReviewTracking = true;
                    const today = new Date();
                    const tomorrow = new Date(today);
                    tomorrow.setDate(today.getDate() + 1);
                    const checkout = new Date(tomorrow);
                    checkout.setDate(tomorrow.getDate() + 3);
                    
                    const formatDate = (d) => {
                        const year = d.getFullYear();
                        const month = String(d.getMonth() + 1).padStart(2, '0');
                        const day = String(d.getDate()).padStart(2, '0');
                        return `${year}-${month}-${day}`;
                    };
                    
                    searchStore.dates = `${formatDate(tomorrow)} to ${formatDate(checkout)}`;
                }

                // If no guests set in store, set default: 2 (capped by maxGuests)
                if (searchStore.guests === 0) {
                    const max = options.maxGuests || 10;
                    const defaultGuests = Math.min(2, max);
                    searchStore.adults = defaultGuests;
                    // Trigger total guest update in store helper usually, but here we set distinct parts
                    // The store watcher or getter handles 'guests' total usually, but search.js has updateGuests()
                    // We should trigger that or manually set guests too if it doesn't auto-update
                    searchStore.guests = defaultGuests; 
                    // Note: search.js doesn't auto-watch adults/children to update guests, 
                    // it has an updateGuests() method but we are outside it.
                    // Ideally we call searchStore.updateGuests() if available or just set it.
                }

                if (searchStore.dates) this.syncDates(searchStore.dates);
                this.syncGuests();
            }

            // Move the single form element to the correct viewport container
            this._moveToViewportContainer();
            window.matchMedia('(min-width: 1024px)').addEventListener('change', () => {
                this._moveToViewportContainer();
            });

            this._onMinStayEvent = (event) => {
                const detail = event?.detail || {};
                if (!detail.listingId || detail.listingId !== this.listingId) {
                    return;
                }

                const minNights = Math.max(1, Number(detail.minNights || 1));
                this.minStayNights = minNights;

                if (detail.state === 'violation') {
                    const nightLabel = minNights === 1 ? 'night' : 'nights';
                    this.errors = {
                        ...(this.errors || {}),
                        general: `Minimum stay is ${minNights} ${nightLabel}. Please select a longer range.`,
                    };
                    this.isCalculated = false;
                    return;
                }

                if (detail.state === 'clear') {
                    this.minStayNights = 1;
                }

                if (this.errors?.general && this.errors.general.startsWith('Minimum stay is ')) {
                    const nextErrors = { ...(this.errors || {}) };
                    delete nextErrors.general;
                    this.errors = nextErrors;
                }
            };
            window.addEventListener('listing:min-stay', this._onMinStayEvent);

            this._onGuestLimitEvent = (event) => {
                const maxGuests = Number(event?.detail?.maxGuests);
                if (!Number.isFinite(maxGuests) || maxGuests <= 0) {
                    return;
                }

                const guestLabel = maxGuests === 1 ? 'guest' : 'guests';
                this.errors = {
                    ...(this.errors || {}),
                    general: `This property allows up to ${maxGuests} ${guestLabel}.`,
                };
                this.isCalculated = false;
            };
            window.addEventListener('listing:guest-limit', this._onGuestLimitEvent);

            // Wait for async CSRF token to be available
            if (window.__csrfToken) {
                this.csrfReady = true;
            } else {
                document.addEventListener('csrf:refreshed', () => {
                    this.csrfReady = true;
                }, { once: true });

                // Safety fallback in case event missed or failed
                setTimeout(() => { this.csrfReady = true; }, 3000);
            }
        },
        

        syncDates(datesStr) {
            if (!datesStr) {
                this.checkIn = '';
                this.checkOut = '';
                this.updatePricing();
                return;
            }
            if (!datesStr.includes(' to ')) return; // Wait for range
            
            const [start, end] = datesStr.split(' to ');
            this.checkIn = start ? start.trim() : '';
            this.checkOut = end ? end.trim() : '';
            // Clear stale availability errors immediately when user changes dates
            this.errors = {};
            this.isCalculated = false;
            this.updatePricing();
        },
        
        syncGuests() {
            const searchStore = Alpine.store('search');
            if (!searchStore) return;
            
            const adults = Number(searchStore.adults);
            const children = Number(searchStore.children);
            const guests = Number(searchStore.guests);

            this.adults = Number.isFinite(adults) && adults >= 0
                ? adults
                : (Number.isFinite(guests) && guests >= 0 ? guests : 0);
            this.children = Number.isFinite(children) && children >= 0 ? children : 0;

            const guestCountError = this.validateGuestCount();
            if (guestCountError) {
                this.errors = {
                    ...(this.errors || {}),
                    general: guestCountError,
                };
                this.isCalculated = false;
                return;
            }

            this.clearGuestCountError();
        },
        
        async updatePricing() {
            // Cancel any pending debounced call
            if (this.pricingTimeout) {
                clearTimeout(this.pricingTimeout);
            }

            const requestId = (this._pricingRequestId = (this._pricingRequestId || 0) + 1);

            this.pricingTimeout = setTimeout(async () => {
                if (!this.checkIn || !this.checkOut) {
                    this.isCalculated = false;
                    this.nightlyRate = 0;
                    this.quote = null;
                    this.updateUrl();
                    return;
                }

                // Wait for CSRF token if needed
                if (!this.csrfReady) {
                    let attempts = 0;
                    while (!this.csrfReady && attempts < 30) { // Max 3s wait
                        await new Promise(r => setTimeout(r, 100));
                        attempts++;
                    }
                }

                // Calculate nights count locally to ensure availability
                const start = new Date(this.checkIn);
                const end = new Date(this.checkOut);
                const nightsCount = Math.max(1, Math.round((end - start) / (1000 * 60 * 60 * 24)));
                
                // Reset state before fetching
                this.isCalculated = false;
                this.quote = null;
                this.errors = {};

                const guestCountError = this.validateGuestCount();
                if (guestCountError) {
                    this.errors = { general: guestCountError };
                    this.updateUrl();
                    return;
                }
                
                this.updateUrl();
                
                // Use this.review() as method is mixed in
                await this.review();

                // Ignore stale responses
                if (requestId !== this._pricingRequestId) {
                    return;
                }

                // If review() set an error (e.g. "not available"), respect it and stop
                if (this.errors?.general) {
                    this.isCalculated = false;
                    return;
                }

                if (this.quote) {
                    // Handle flat structure from Guesty API wrapper
                    if (this.quote.total !== undefined) {
                        this.currency = 'USD'; // Default or find in invoiceItems
                        if (this.quote.invoiceItems?.[0]?.currency) {
                            this.currency = this.quote.invoiceItems[0].currency;
                        }

                        // basePrice is typically the total accommodation fare
                        const basePrice = this.quote.basePrice || 0;
                        this.nightlyRate = basePrice / nightsCount;
                        this.total = this.quote.total || 0;
                        
                        // Taxes & Fees = Total - Base Price
                        // Or sum of taxes + fees
                        this.taxesAndFees = (this.quote.taxes || 0) + (this.quote.fees || 0);
                        
                        this.quote.nightsCount = nightsCount; // Ensure template has access
                        this.isCalculated = true;
                    }
                    // Handle fallback/legacy structure if needed (though logs confirm flat)
                    else if (this.quote.money) {
                        this.currency = this.quote.money.currency || 'USD';
                        this.nightlyRate = this.quote.money.fareAccommodation / this.quote.nightsCount || 0;
                        this.total = this.quote.money.fareTotal || 0;
                        this.taxesAndFees = this.total - this.quote.money.fareAccommodation;
                        this.isCalculated = true;
                    } else {
                        this.isCalculated = false;
                        this.errors = this.errors?.general
                            ? this.errors
                            : { general: 'Unable to load pricing for the selected dates. Please try again.' };
                    }
                } else {
                    this.isCalculated = false;
                    this.errors = this.errors?.general
                        ? this.errors
                        : { general: 'Unable to load pricing for the selected dates. Please try again.' };
                }


            }, 200);
        },

        _moveToViewportContainer() {
            const isLg = window.matchMedia('(min-width: 1024px)').matches;
            const targetId = isLg ? 'desktop-booking-form-container' : 'mobile-booking-form-container';
            const target = document.getElementById(targetId);
            if (target && this.$el.parentElement !== target) {
                target.appendChild(this.$el);
            }
        },

        updateUrl() {
            const url = new URL(window.location);
            if (this.checkIn && this.checkOut) {
                url.searchParams.set('dates', `${this.checkIn} to ${this.checkOut}`);
            } else {
                url.searchParams.delete('dates');
            }
            
            if (this.adults > 0) url.searchParams.set('adults', this.adults);
            if (this.children > 0) url.searchParams.set('children', this.children);
            
            // Sync guests param for consistency with search bar
            const totalGuests = (parseInt(this.adults) || 1) + (parseInt(this.children) || 0);
            url.searchParams.set('guests', totalGuests);
            
            window.history.replaceState({}, '', url);
        },
        
        reserve(e) {
            this.isSubmitting = false;

            console.log('Reserve button clicked', {
                isCalculated: this.isCalculated,
                errors: this.errors,
                quoteId: this.quote?.quoteId,
                basePrice: this.basePrice
            });

            // Validate quote before proceeding
            if (!this.isCalculated) {
                console.log('Quote not calculated');
                e.preventDefault();
                this.errors = { general: 'Please select check-in and check-out dates to continue.' };
                return;
            }

            if (Object.keys(this.errors).length > 0) {
                 console.log('Validation errors present', this.errors);
                 // If only error is 'general', we might have logic here, but for now we block.
                 // Legacy hack removed: we trust the error now
                 if (Object.keys(this.errors).length === 1 && this.errors.general) {
                      console.log('Blocking submission due to general error', this.errors.general);
                 } else {
                      console.log('Blocking submission due to errors');
                 }
                 e.preventDefault();
                 return;
            }

            this.isSubmitting = true;
            console.log('Submitting form naturally...');
        },

        formatPrice(amount) {
            return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: this.currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(amount);
        }
    };
}
